import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import IconAction from "@/components/IconAction";
import { BadgeCheck, Plus, Trash2, FileText, Pencil, X } from "lucide-react";
import { SettingsHeader, SettingsSaveBar } from "./_shared";

export default function FreelanceSettings() {
  const [s, setS] = useState(null);
  const [freelanceDriveUrl, setFreelanceDriveUrl] = useState("");
  const [portageCompanies, setPortageCompanies] = useState([]);
  const [portageDraft, setPortageDraft] = useState({ name: "", address: "", phone: "", email: "", ice: "", notes: "" });
  // Iter-91: inline-edit on the portage row. `editing` is the company_id currently being edited,
  // `editDraft` holds the in-progress changes until Save.
  const [editing, setEditing] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: "", address: "", phone: "", email: "", ice: "", notes: "" });
  // Iter-220 — read-only view modal opened by clicking a company name in the list.
  const [viewing, setViewing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [r, pc] = await Promise.all([
      api.get("/settings"),
      api.get("/portage-companies").catch(() => ({ data: [] })),
    ]);
    setS(r.data);
    setPortageCompanies(pc.data || []);
    setFreelanceDriveUrl(r.data.freelance_invoice_drive_folder_url || "");
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { freelance_invoice_drive_folder_url: freelanceDriveUrl });
      toast.success("Freelance Drive saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const addPortage = async () => {
    if (!portageDraft.name.trim()) return toast.error("Name required");
    try {
      await api.post("/portage-companies", portageDraft);
      toast.success("Company added");
      setPortageDraft({ name: "", address: "", phone: "", email: "", ice: "", notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const removePortage = async (c) => {
    if (!window.confirm(`Delete le cabinet « ${c.name} » ?`)) return;
    try { await api.delete(`/portage-companies/${c.company_id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const startEdit = (c) => {
    setEditing(c.company_id);
    setEditDraft({
      name: c.name || "", address: c.address || "", phone: c.phone || "",
      email: c.email || "", ice: c.ice || "", notes: c.notes || "",
    });
  };
  const cancelEdit = () => { setEditing(null); setEditDraft({ name: "", address: "", phone: "", email: "", ice: "", notes: "" }); };
  const saveEdit = async (company_id) => {
    if (!editDraft.name.trim()) return toast.error("Name required");
    try {
      await api.patch(`/portage-companies/${company_id}`, editDraft);
      toast.success("Company updated");
      cancelEdit();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  if (!s) return <div className="p-12 text-zinc-500">Loading…</div>;

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[900px]" data-testid="settings-page-freelance">
      <SettingsHeader
        kicker="Freelance"
        title="Portage companies & Drive"
        description="Catalogue of companies and shared Drive folder for freelance invoices."
      />

      {/* Portage companies */}
      <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-5 mb-6" data-testid="portage-companies-block">
        <div className="flex items-center gap-3 text-zinc-900">
          <BadgeCheck size={18} className="text-zinc-700" />
          <h2 className="font-heading font-bold text-lg">Portage companies (freelances)</h2>
        </div>
        <p className="text-xs text-zinc-500 max-w-2xl">Catalogue of portage companies used by freelances. Each freelance profile must be attached to a company.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="mb-2 block">Company name *</Label>
            <Input value={portageDraft.name} onChange={(e) => setPortageDraft({ ...portageDraft, name: e.target.value })} placeholder="ex. UMAlis" data-testid="portage-name" />
          </div>
          <div className="md:col-span-2">
            <Label className="mb-2 block">Address</Label>
            <Input value={portageDraft.address} onChange={(e) => setPortageDraft({ ...portageDraft, address: e.target.value })} data-testid="portage-address" />
          </div>
          <div>
            <Label className="mb-2 block">Phone</Label>
            <Input value={portageDraft.phone} onChange={(e) => setPortageDraft({ ...portageDraft, phone: e.target.value })} data-testid="portage-phone" />
          </div>
          <div>
            <Label className="mb-2 block">Contact email</Label>
            <Input type="email" value={portageDraft.email} onChange={(e) => setPortageDraft({ ...portageDraft, email: e.target.value })} data-testid="portage-email" />
          </div>
          <div>
            <Label className="mb-2 block">ICE</Label>
            <Input value={portageDraft.ice} onChange={(e) => setPortageDraft({ ...portageDraft, ice: e.target.value })} data-testid="portage-ice" />
          </div>
        </div>
        <button onClick={addPortage} data-testid="portage-add" className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800">
          <Plus size={14} /> Add company
        </button>
        <div className="border border-zinc-200 rounded-md md:overflow-x-auto md:overflow-hidden">
          <div className="hidden md:grid md:grid-cols-12 md:min-w-[760px] px-4 py-2 text-[10px] uppercase tracking-widest text-zinc-500 font-mono bg-zinc-50 border-b border-zinc-200">
            <div className="col-span-3">Nom</div>
            <div className="col-span-3">Address</div>
            <div className="col-span-2">Phone / Email</div>
            <div className="col-span-3">ICE</div>
            <div className="col-span-1 text-right">—</div>
          </div>
          {portageCompanies.length === 0 && <div className="px-4 py-6 text-sm text-zinc-500 text-center">No portage company yet.</div>}
          {portageCompanies.map((c) => (
            editing === c.company_id ? (
              <div key={c.company_id} data-testid={`portage-edit-${c.company_id}`} className="flex flex-col gap-2 md:grid md:grid-cols-12 md:items-start md:gap-2 md:min-w-[760px] px-4 py-3 text-xs border-b border-zinc-100 last:border-b-0 bg-brand-cream/40">
                <div className="col-span-3">
                  <Input className="h-8 text-xs" value={editDraft.name} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} placeholder="Name" data-testid={`portage-edit-name-${c.company_id}`} />
                </div>
                <div className="col-span-3">
                  <Input className="h-8 text-xs" value={editDraft.address} onChange={(e) => setEditDraft({ ...editDraft, address: e.target.value })} placeholder="Address" />
                </div>
                <div className="col-span-2 space-y-1">
                  <Input className="h-8 text-xs" value={editDraft.phone} onChange={(e) => setEditDraft({ ...editDraft, phone: e.target.value })} placeholder="Phone" />
                  <Input className="h-8 text-xs font-mono" value={editDraft.email} onChange={(e) => setEditDraft({ ...editDraft, email: e.target.value })} placeholder="Email" />
                </div>
                <div className="col-span-3">
                  <Input className="h-8 text-xs font-mono" value={editDraft.ice} onChange={(e) => setEditDraft({ ...editDraft, ice: e.target.value })} placeholder="ICE" />
                </div>
                <div className="col-span-1 flex items-center justify-end gap-1 pt-1">
                  <button onClick={() => saveEdit(c.company_id)} data-testid={`portage-edit-save-${c.company_id}`} className="px-2 py-1 text-[10px] rounded bg-brand-navy text-white hover:bg-brand-navy/90 font-medium">Save</button>
                  <button onClick={cancelEdit} data-testid={`portage-edit-cancel-${c.company_id}`} className="p-1.5 text-zinc-500 hover:text-zinc-900 rounded"><X size={13} /></button>
                </div>
              </div>
            ) : (
              <div key={c.company_id} data-testid={`portage-row-${c.company_id}`} className="flex flex-col gap-2 md:grid md:grid-cols-12 md:items-center md:gap-0 md:min-w-[760px] px-4 py-2 text-xs border-b border-zinc-100 last:border-b-0">
                <div className="col-span-3 font-medium text-sm">
                  <button
                    type="button"
                    onClick={() => setViewing(c)}
                    data-testid={`portage-view-${c.company_id}`}
                    className="text-left text-brand-navy hover:text-brand-orange hover:underline focus:outline-none focus:ring-2 focus:ring-brand-orange/40 rounded"
                    title="Voir la fiche du cabinet"
                  >
                    {c.name}
                  </button>
                </div>
                <div className="col-span-3 text-zinc-600 truncate">{c.address || "—"}</div>
                <div className="col-span-2 text-zinc-600 truncate">
                  {c.phone && <div>{c.phone}</div>}
                  {c.email && <div className="font-mono text-[11px]">{c.email}</div>}
                  {!c.phone && !c.email && "—"}
                </div>
                <div className="col-span-3 text-zinc-600 font-mono text-[11px]">{c.ice || "—"}</div>
                <div className="col-span-1 text-right flex items-center justify-end gap-1">
                  <IconAction icon={Pencil} testid={`portage-edit-${c.company_id}`} label="Modifier la société de portage" onClick={() => startEdit(c)} />
                  <IconAction icon={Trash2} tone="danger" testid={`portage-delete-${c.company_id}`} label="Supprimer la société de portage" onClick={() => removePortage(c)} />
                </div>
              </div>
            )
          ))}
        </div>
      </div>

      {/* Drive folder */}
      <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-4 mb-6" data-testid="freelance-drive-block">
        <div className="flex items-center gap-3 text-zinc-900">
          <FileText size={18} className="text-brand-orange" />
          <h2 className="font-heading font-bold text-lg">Global Drive — freelance invoices</h2>
        </div>
        <p className="text-xs text-zinc-500 max-w-2xl">
          Google Drive link of the shared admin folder that will be used <strong>by default</strong> to archive freelance invoices and contracts.
          Each freelance profile can override this link in Users &amp; Roles si besoin (ex. dossier spécifique par cabinet de portage).
        </p>
        <div>
          <Label className="mb-2 block">Shared Drive folder URL</Label>
          <Input
            type="url"
            placeholder="https://drive.google.com/drive/folders/..."
            value={freelanceDriveUrl}
            onChange={(e) => setFreelanceDriveUrl(e.target.value)}
            data-testid="settings-freelance-drive-url"
          />
          <p className="text-[11px] text-zinc-500 mt-1">Make sure to grant <code>baim-mission-control@…</code> editor access on this folder.</p>
        </div>
      </div>

      <SettingsSaveBar onSave={save} onReset={load} saving={saving} testId="settings-save" />

      {/* Iter-220 — Read-only fiche modal for a portage company. */}
      <Dialog open={!!viewing} onOpenChange={(open) => { if (!open) setViewing(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="portage-view-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BadgeCheck size={18} className="text-brand-orange" />
              {viewing?.name || "Cabinet de portage"}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Fiche du cabinet en lecture seule. Sélectionnez le texte pour copier les informations.
            </DialogDescription>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3 text-sm" data-testid="portage-view-content">
              <ReadOnlyField label="Nom" value={viewing.name} testid="portage-view-name" />
              <ReadOnlyField label="Adresse" value={viewing.address} testid="portage-view-address" multiline />
              <div className="grid grid-cols-2 gap-3">
                <ReadOnlyField label="Téléphone" value={viewing.phone} testid="portage-view-phone" />
                <ReadOnlyField label="Email" value={viewing.email} testid="portage-view-email" mono />
              </div>
              <ReadOnlyField label="ICE" value={viewing.ice} testid="portage-view-ice" mono />
              {viewing.notes && <ReadOnlyField label="Notes" value={viewing.notes} testid="portage-view-notes" multiline />}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <button
              type="button"
              onClick={() => setViewing(null)}
              data-testid="portage-view-close"
              className="px-4 h-9 rounded-md border border-zinc-300 text-sm text-zinc-700 hover:bg-zinc-50"
            >
              Fermer
            </button>
            <button
              type="button"
              onClick={() => { startEdit(viewing); setViewing(null); }}
              data-testid="portage-view-edit"
              className="inline-flex items-center gap-2 px-4 h-9 rounded-md bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navy/90"
            >
              <Pencil size={14} /> Éditer
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReadOnlyField({ label, value, testid, multiline = false, mono = false }) {
  const display = value || "—";
  return (
    <div>
      <Label className="mb-1 block text-[11px] uppercase tracking-widest text-zinc-500">{label}</Label>
      <div
        data-testid={testid}
        className={`px-3 py-2 rounded-md border border-zinc-200 bg-zinc-50 text-zinc-800 select-text ${mono ? "font-mono text-[12px]" : "text-sm"} ${multiline ? "whitespace-pre-wrap min-h-[2.25rem]" : "truncate"}`}
      >
        {display}
      </div>
    </div>
  );
}
